//@flow
import React, {useState, useEffect} from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import {Typography} from '@material-ui/core';
import {colors} from '../../../../assets/styles/colors';

import {getCurrencyByCountry} from '../../../on-boarding/utils';

import CustomModal from '../../../../elements/CustomModal/CustomModal';
import {useStoreCountry} from '../../../country/utils';
import {useStoreInfo} from '../../../store/utils';
import {useTypedSelector} from '../../../../store';
import {
  useCurrentSubscription,
  useBillingPortalLink,
  useIsInEndSubscriptionDays,
} from '../../utils';

import {actions as CheckoutActions} from '../../../checkout/duck';
import {actions as TiersActions} from '../../../tiers/duck';
import {useBindedActions} from '../../../../utils/globalStore';
import {connect} from 'react-redux';

import type {TierDetail} from '../../api';
import type {RootState} from '../../..';

import {FREE_TRIAL_TIERS, type PaymentProvider} from '../../../../entities';
import CustomButton from '../../../../elements/CustomButton/CustomButton';

const useStyles = makeStyles(theme => ({
  root: {
    maxWidth: 500,
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(4),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2),
    },
    [theme.breakpoints.down('sm')]: {
      marginBottom: 64,
    },
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  title: {
    fontSize: 24,
    fontWeight: 600,
    color: colors.grey1000,
    textAlign: 'center',
  },
  yellowWrap: {
    padding: theme.spacing(1, 2),
    backgroundColor: '#FEF5E1',
    borderRadius: 20,
    marginTop: 16,
    '& p': {
      textAlign: 'center',
      fontSize: 16,
      fontWeight: 500,
    },
  },
  button: {
    width: 200,
    borderRadius: 20,
    marginTop: 16,
  },
}));

const actions = {...CheckoutActions, ...TiersActions};

export default connect(
  (state: RootState) => ({
    basicPlans: state.tiersList.basicPlans,
    proPlusPlans: state.tiersList.proPlusPlans,
    paymentProviders: state.checkout.paymentProviders,
  }),
  null
)(TiersListModal);

type Props = {
  basicPlans: TierDetail[],
  proPlusPlans: TierDetail[],
  paymentProviders?: PaymentProvider[],
};

const AFTER_DAYS_APPEARS_MODAL = 7;

function TiersListModal(props: Props) {
  const {paymentProviders} = props;

  const {pricingTableLink} = useBillingPortalLink();

  const {
    fetchCards,
    fetchPaymentProviders,
    setPlanSelectedCurrency,
  } = useBindedActions(actions);

  const subscription = useCurrentSubscription();

  const isRemainingDays = useIsInEndSubscriptionDays(AFTER_DAYS_APPEARS_MODAL);
  const store = useStoreInfo();
  const country = useStoreCountry();
  const cardProfiles = useTypedSelector(state => state.checkout.cardProfiles);

  const isFetchingCards = useTypedSelector(
    state => state.checkout.isFetchingCardProfiles
  );
  const hasAtLeastOneCard = cardProfiles && cardProfiles.length > 0;
  const classes = useStyles();

  const [open, setOpen] = useState<boolean>(false);

  const isFreeTrialSubscription = !!FREE_TRIAL_TIERS.includes(
    store && store.storeTier.name
  );

  useEffect(() => {
    if (country) {
      const c = getCurrencyByCountry(country.code);
      if (c && paymentProviders && paymentProviders.length) {
        for (let i = 0; i < paymentProviders.length; i++) {
          if (paymentProviders[i].currency.code === c) {
            setPlanSelectedCurrency({currency: c});
          }
        }
        setPlanSelectedCurrency({currency: 'USD'});
      }
    }
  }, [country, paymentProviders, setPlanSelectedCurrency]);

  useEffect(() => {
    if (country)
      fetchPaymentProviders({providersQuery: {country: country.code}});
  }, [country, fetchPaymentProviders]);

  useEffect(() => {
    if (store) {
      if (!cardProfiles) fetchCards();
    }
  }, [cardProfiles, fetchCards, store]);

  useEffect(() => {
    if (
      subscription &&
      isFreeTrialSubscription &&
      !isFetchingCards &&
      !hasAtLeastOneCard
    ) {
      if (isRemainingDays) {
        setOpen(true);
      }
    }
  }, [
    hasAtLeastOneCard,
    isFetchingCards,
    isFreeTrialSubscription,
    isRemainingDays,
    subscription,
  ]);

  return (
    <CustomModal open={open} handleCloseModal={() => setOpen(false)}>
      <div className={classes.root}>
        <div className={classes.content}>
          <Typography className={classes.title}>
            ¡No te quedes sin tu TiendaDa! Selecciona tu plan hoy.
          </Typography>

          <div className={classes.yellowWrap}>
            <Typography>
              Elige tu plan y te cobraremos una vez finalize tu periodo de
              pruebas.
            </Typography>
          </div>
          <div style={{marginTop: '16px'}}>
            {pricingTableLink && (
              <CustomButton
                variant="contained"
                color="primary"
                className={classes.button}
                style={{marginTop: 16}}
                to="/profile"
              >
                Ver planes
              </CustomButton>
            )}
          </div>
        </div>
      </div>
    </CustomModal>
  );
}
